import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import isArray from 'lodash/isArray';
import sortBy from 'lodash/sortBy';
import { Search } from 'lucide-react';
import Breadcrumb from '../components/Common/Breadcrumb';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Spinner } from '../components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Actions as ORGANISATIONACTIONS } from '../store/actions/organisationActions';
import { Actions as FACILITYACTIONS } from '../store/actions/facilityActions';
import createSignedRequest from '../store/actions/createSignedRequest';

const { apiURL } = require('../store/actions/apiURL');

const breadcrumbItems = [{ label: 'Industries & Facility Types', link: '/industries-and-facility-types' }];

const matchesQuery = (value, query) => {
  if (!query) return true;
  return String(value || '')
    .toLowerCase()
    .includes(query.toLowerCase().trim());
};

const isIncludedIndustry = (industry) => {
  if (industry?.include === undefined || industry?.include === null) return true;
  return industry.include === true || industry.include === 'yes';
};

const readCatalogueItems = (payload) => {
  if (isArray(payload)) return payload;
  const items = payload?.items?.message ? payload.items.message : payload?.items;
  return isArray(items) ? items : null;
};

/**
 * Read-only reference of organisation industries and facility types.
 * Industries come from GET /impact/industries. Facility types prefer
 * GET /admin/portal/facilityType (same source as admin/facilitiesType) and
 * fall back to GET /impact/facilityTypes for users without admin access.
 */
const IndustriesAndFacilityTypes = ({
  industries,
  facilityTypes,
  canFetchAdminFacilityTypes,
  listIndustries,
  listFacilityTypes,
}) => {
  const [industryQuery, setIndustryQuery] = useState('');
  const [facilityQuery, setFacilityQuery] = useState('');
  const [adminFacilityTypes, setAdminFacilityTypes] = useState(null);

  useEffect(() => {
    if (!isArray(industries)) listIndustries();
  }, [industries, listIndustries]);

  useEffect(() => {
    if (!canFetchAdminFacilityTypes) {
      if (!isArray(facilityTypes)) listFacilityTypes();
      return;
    }

    let cancelled = false;

    const loadFacilityTypes = async () => {
      try {
        const response = await axios(await createSignedRequest('GET', `${apiURL}/admin/portal/facilityType`, null));
        const items = readCatalogueItems(response.data);
        if (!cancelled && items) {
          setAdminFacilityTypes(items);
          return;
        }
      } catch (e) {
        // Fall back to the public facility types catalogue.
      }

      if (!cancelled && !isArray(facilityTypes)) listFacilityTypes();
    };

    loadFacilityTypes();
    return () => {
      cancelled = true;
    };
  }, [canFetchAdminFacilityTypes, listFacilityTypes]);

  const sourceFacilityTypes = isArray(adminFacilityTypes) ? adminFacilityTypes : facilityTypes;

  const visibleIndustries = useMemo(() => {
    if (!isArray(industries)) return [];
    return sortBy(industries.filter(isIncludedIndustry), ['name']).filter((industry) =>
      matchesQuery(industry?.name, industryQuery),
    );
  }, [industries, industryQuery]);

  const visibleFacilityTypes = useMemo(() => {
    if (!isArray(sourceFacilityTypes)) return [];
    return sortBy(sourceFacilityTypes, [(item) => item?.name || item?.id]).filter(
      (facilityType) =>
        matchesQuery(facilityType?.name || facilityType?.id, facilityQuery) ||
        matchesQuery(facilityType?.description, facilityQuery),
    );
  }, [sourceFacilityTypes, facilityQuery]);

  const industriesLoaded = isArray(industries);
  const facilityTypesLoaded = isArray(sourceFacilityTypes);

  return (
    <React.Fragment>
      <Breadcrumb items={breadcrumbItems} />
      <Card className='border rounded-md hover:translate-y-0'>
        <CardHeader>
          <CardTitle className='text-xl font-semibold'>Industries & Facility Types</CardTitle>
          <p className='text-sm text-muted-foreground pt-1'>
            Reference list of organisation industries and facility types used in Compare Your Footprint.
          </p>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue='industries'>
            <TabsList>
              <TabsTrigger id='industries-guide-tab' value='industries'>
                Industries
              </TabsTrigger>
              <TabsTrigger id='facility-types-guide-tab' value='facilityTypes'>
                Facility Types
              </TabsTrigger>
            </TabsList>

            <TabsContent value='industries'>
              <div className='relative mb-4 max-w-sm'>
                <Search
                  size={16}
                  strokeWidth={1.5}
                  className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground'
                />
                <Input
                  id='industry-guide-search'
                  placeholder='Search industries'
                  className='pr-9'
                  value={industryQuery}
                  onChange={(e) => setIndustryQuery(e.target.value)}
                />
              </div>
              {!industriesLoaded ? (
                <div className='flex justify-center py-10'>
                  <Spinner size='large' />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Industry</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleIndustries.length === 0 && (
                      <TableRow>
                        <TableCell className='text-center text-muted-foreground py-6'>
                          No industries match your search.
                        </TableCell>
                      </TableRow>
                    )}
                    {visibleIndustries.map((industry) => (
                      <TableRow key={industry.id || industry.name}>
                        <TableCell className='font-medium text-foreground'>{industry.name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value='facilityTypes'>
              <div className='relative mb-4 max-w-sm'>
                <Search
                  size={16}
                  strokeWidth={1.5}
                  className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground'
                />
                <Input
                  id='facility-type-guide-search'
                  placeholder='Search facility types'
                  className='pr-9'
                  value={facilityQuery}
                  onChange={(e) => setFacilityQuery(e.target.value)}
                />
              </div>
              {!facilityTypesLoaded ? (
                <div className='flex justify-center py-10'>
                  <Spinner size='large' />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className='w-1/3'>Facility Name</TableHead>
                      <TableHead>Facility Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleFacilityTypes.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} className='text-center text-muted-foreground py-6'>
                          No facility types match your search.
                        </TableCell>
                      </TableRow>
                    )}
                    {visibleFacilityTypes.map((facilityType) => (
                      <TableRow key={facilityType.id || facilityType.name}>
                        <TableCell className='font-medium text-foreground'>
                          {facilityType.name || facilityType.id}
                        </TableCell>
                        <TableCell className='text-foreground whitespace-normal'>
                          {facilityType.description || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  const { cyfAdmin, whiteLabelAdmin, consultantAdmin, permissions } = state.profile?.details || {};

  return {
    industries: state.organisation?.industries,
    facilityTypes: state.facility?.types,
    canFetchAdminFacilityTypes:
      !!cyfAdmin || !!whiteLabelAdmin || !!consultantAdmin || !!permissions?.portal?.includes('VIEW_ADMIN'),
  };
};

const mapDispatchToProps = (dispatch) => ({
  listIndustries: () => dispatch(ORGANISATIONACTIONS.listIndustries()),
  listFacilityTypes: () => dispatch(FACILITYACTIONS.getFacilityTypes()),
});

export default connect(mapStateToProps, mapDispatchToProps)(IndustriesAndFacilityTypes);
